/**
 * Password Validation Utilities
 *
 * Password requirements:
 * - Minimum 8 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 digit
 * - At least 1 special character
 */

export interface PasswordValidation {
  isValid: boolean;
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasDigit: boolean;
  hasSpecialChar: boolean;
}

const MIN_LENGTH = 8;
const SPECIAL_CHARS = '!"#$%&\'()*+,./?:;<=>?@[]^_\\`{|}~-';

/**
 * Validate a password against all requirements
 */
export function validatePassword(password: string): PasswordValidation {
  const hasMinLength = password.length >= MIN_LENGTH;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasDigit = /\d/.test(password);
  const hasSpecialChar = new RegExp(`[${SPECIAL_CHARS.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password);

  return {
    isValid: hasMinLength && hasUppercase && hasLowercase && hasDigit && hasSpecialChar,
    hasMinLength,
    hasUppercase,
    hasLowercase,
    hasDigit,
    hasSpecialChar,
  };
}

/**
 * Generate a secure random password that meets all requirements
 */
export function generatePassword(): string {
  const length = 16;
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const specials = SPECIAL_CHARS;

  const allChars = uppercase + lowercase + digits + specials;

  // Get random values for character selection
  const randomValues = new Uint32Array(length);
  window.crypto.getRandomValues(randomValues);

  // Ensure at least one of each required character type
  let password = '';
  password += uppercase[randomValues[0] % uppercase.length];
  password += lowercase[randomValues[1] % lowercase.length];
  password += digits[randomValues[2] % digits.length];
  password += specials[randomValues[3] % specials.length];

  // Fill the rest with random characters
  for (let i = 4; i < length; i++) {
    password += allChars[randomValues[i] % allChars.length];
  }

  // Shuffle the password to avoid predictable pattern
  const shuffleValues = new Uint32Array(length);
  window.crypto.getRandomValues(shuffleValues);
  const chars = password.split('');
  for (let i = chars.length - 1; i > 0; i--) {
    const j = shuffleValues[i] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }

  return chars.join('');
}
